import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge, KpiGrid, AssignDialog, VehicleOpsButtons } from "@/components/transport/transportShared";

const ACTIVE_REQ = ["ASSIGNED", "LOADING", "FULL", "DEPARTED", "AT_PASHU_HAAT"];

/** Helper Transport Dashboard — live vehicle board (20s polling), big simple actions. */
export default function HelperTransport() {
  const [kpi, setKpi] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [assignFor, setAssignFor] = useState(null);

  const refresh = useCallback(() => {
    Promise.all([
      api.get("/transport/dashboard"),
      api.get("/transport/vehicles"),
      api.get("/transport/drivers"),
      api.get("/transport/requests"),
    ]).then(([k, v, d, r]) => { setKpi(k.data); setVehicles(v.data); setDrivers(d.data); setRequests(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load transport data"));
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 20000); // live board
    return () => clearInterval(id);
  }, [refresh]);

  const driverName = (id) => drivers.find((d) => d.driver_id === id)?.name || "—";
  const activeReqByVehicle = Object.fromEntries(
    requests.filter((r) => ACTIVE_REQ.includes(r.status) && r.assigned_vehicle_id).map((r) => [r.assigned_vehicle_id, r]),
  );
  const pending = requests.filter((r) => ["PENDING", "MATCHING"].includes(r.status));

  return (
    <DashboardShell title="Transport" subtitle="Live vehicle board — updates every 20s">
      <div className="space-y-6">
        <KpiGrid data={kpi} compact />

        {pending.length > 0 && (
          <div className="am-card p-4 space-y-3" data-testid="tr-helper-pending">
            <h2 className="font-display text-lg font-bold">Waiting for a vehicle ({pending.length})</h2>
            {pending.map((r) => (
              <div key={r.request_id} className="rounded-xl border border-border p-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm">{r.customer_name} · {r.animal_quantity} {r.animal_type}</div>
                  <div className="text-xs text-muted-foreground">{r.pickup_area} → {r.destination_pashu_haat} · needs capacity {r.required_capacity}</div>
                </div>
                <Button size="sm" className="am-btn-primary rounded-full" data-testid={`tr-helper-assign-${r.request_id}`} onClick={() => setAssignFor(r)}>
                  Assign Vehicle
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="am-card overflow-x-auto" data-testid="tr-helper-vehicles">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                {["Vehicle", "Driver", "Area", "Capacity", "Loaded", "Remaining", "Status", "Trip", "Actions"].map((c) => (
                  <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vehicles.filter((v) => v.is_active).map((v) => {
                const trip = activeReqByVehicle[v.vehicle_id];
                return (
                  <tr key={v.vehicle_id} className="border-b last:border-0" data-testid={`tr-helper-row-${v.vehicle_id}`}>
                    <td className="px-3 py-3 font-bold whitespace-nowrap">{v.registration_number}<div className="text-xs font-normal text-muted-foreground">{v.vehicle_type}</div></td>
                    <td className="px-3 py-3 whitespace-nowrap">{driverName(v.driver_id)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">{v.operating_area}</td>
                    <td className="px-3 py-3">{v.animal_capacity}</td>
                    <td className="px-3 py-3 font-bold" data-testid={`tr-helper-loaded-${v.vehicle_id}`}>{v.loaded_animals}</td>
                    <td className="px-3 py-3 font-bold" data-testid={`tr-helper-remaining-${v.vehicle_id}`}>{v.remaining_capacity}</td>
                    <td className="px-3 py-3"><StatusBadge status={v.status} /></td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {trip ? <>{trip.pickup_area} → {trip.destination_pashu_haat}{trip.status === "ASSIGNED" && <button className="block text-primary font-bold underline-offset-2 hover:underline" data-testid={`tr-helper-reassign-${trip.request_id}`} onClick={() => setAssignFor(trip)}>Reassign</button>}</> : "—"}
                    </td>
                    <td className="px-3 py-3"><VehicleOpsButtons vehicle={v} onDone={refresh} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {vehicles.filter((v) => v.is_active).length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">No active vehicles yet. The market admin adds vehicles.</p>
          )}
        </div>
      </div>
      <AssignDialog request={assignFor} open={!!assignFor} setOpen={() => setAssignFor(null)} onDone={refresh} />
    </DashboardShell>
  );
}
